<template>
	<TheStdLayout :title="$tc('Activity', 2)">

		<ActivityEditor v-if="selectedActivityForEditing" :activity="selectedActivityForEditing" @requestClose="hideEditor" />

		<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">
			<FloatingPlusButton :pulsate="!isLoading && activitiesStore.activities.length === 0" @push="handleActivityCreateRequest" />
			<ActivitiesFilter class="block my-2 @3xl:mt-0 @3xl:pr-12" :filter="filter" fieldClasses="bg-base-100 @3xl:bg-base" @change="handleChangedFilter" @reset="resetFilter" />

			<EmptyState v-if="!isLoading && activitiesStore.activities.length === 0" :title="hasActiveFilter ? $t('No activities found') : $t('No activities yet')" :description="hasActiveFilter ? $t('Try a different search term') : $t('Start with creating your first activity')" />


			<table v-else class="divide-y divide-base-200 w-full">

				<thead>
					<tr>
						<th><span class="sr-only">{{$t('Icon')}}</span></th>
						<th scope="col" class="table-col-title">
							{{ $t('Name') }}
						</th>
						<th scope="col" class="px-6 py-3">
							<span class="sr-only">{{$t('Actions')}}</span>
						</th>
					</tr>
				</thead>

				<tbody class="bg-base divide-y divide-base-200">

					<tr v-if="isLoading">
						<td colspan="3">
							<Skeleton content-type="userlist" :items="3" class="m-4" thumbnail-classes="mask mask-squircle" />
						</td>
					</tr>

					<template v-else>
						<tr v-for="activity in activitiesStore.activities" :key="activity.id">
							<td class="px-4 py-3 print:py-1 w-16">
								<ActivitySymbol :activity="activity" class="w-14 bg-base-100 rounded-lg" />
							</td>
							<td class="px-4 py-3 print:py-1 whitespace-nowrap" valign="top">
								{{ activity.name }}
							<div class="text-sm text-base-content-light lowercase" :class="[activity.seats == 0? 'text-danger':'']">
									{{ activity.seats }} {{ $tc('Seat', activity.seats) }}
								</div>
							</td>
							<td class="px-4 py-3 print:py-1 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2 sm:gap-4">
								<div class="flex-row print:hidden">
									<button class="btn btn-ghost btn-circle bg-base-100" @click="handleActivityEditRequest(activity)">
										<PencilIcon class="w-5 h-5"></PencilIcon>
									</button>
								</div>
							</td>
						</tr>
					</template>

				</tbody>
			</table>
		</div>

	</TheStdLayout>
</template>

<script lang="ts">

import Skeleton from '@/components/ui/Skeleton.vue';
import ActivitySymbol from "@/components/ui/symbols/ActivitySymbol.vue";
import ActivitiesFilter from "@/components/ActivitiesFilter.vue";

import {mapStores} from "pinia";
import {useIconsStore} from "@/stores/Icons.store";
import {useActivitiesStore} from "@/stores/Activities.store";
import {useIconLibrariesStore} from "@/stores/IconLibraries.store";

import ActivityEditor from "@/components/ActivityEditor.vue";
import { PlusIcon, PencilIcon } from '@heroicons/vue/24/outline';

export default {

	data() {
		return {
			selectedActivityForEditing: null,
		};
	},

	components: {
		Skeleton,
		ActivitySymbol,
		ActivityEditor,
		PlusIcon, PencilIcon,
		ActivitiesFilter,
	},

	computed: {
		...mapStores(useIconsStore, useActivitiesStore, useIconLibrariesStore),

		filter() {
			return this.activitiesStore.getCurrentFilter();
		},

		isLoading() {
			return this.activitiesStore.loading;
		},

		hasActiveFilter() {
			return !this.filter.isEmpty();
		},
	},

	mounted() {
		this.activitiesStore.load();
	},

	methods: {

		handleActivityCreateRequest() {
			this.selectedActivityForEditing = this.activitiesStore.new();
		},

		handleActivityEditRequest(activity) {
			this.selectedActivityForEditing = activity;
		},

		hideEditor() {
			this.selectedActivityForEditing = null;
		},

		handleChangedFilter(filter) {
			this.activitiesStore.applyFilter(filter);
		},

		resetFilter() {
			this.activitiesStore.resetFilter();
		},

	},
};

</script>
